import { useCallback } from 'react';

const STORAGE_KEY = 'feedbackhub_comment_drafts';
const EXPIRY_HOURS = 24;

interface CommentDraft {
  postId: string;
  parentId?: string;
  value: string;
  imageURLs: string[];
  savedAt: number;
}

type DraftsMap = Record<string, CommentDraft>;

function getDraftKey(postId: string, parentId?: string): string {
  return parentId ? `${postId}:${parentId}` : postId;
}

function getAllDrafts(): DraftsMap {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    return JSON.parse(stored) as DraftsMap;
  } catch {
    return {};
  }
}

function setAllDrafts(drafts: DraftsMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  } catch {
    // Ignore storage errors
  }
}

function isExpired(savedAt: number): boolean {
  const expiryMs = EXPIRY_HOURS * 60 * 60 * 1000;
  return Date.now() - savedAt > expiryMs;
}

export function useCommentDraft() {
  const saveDraft = useCallback(
    (postId: string, parentId: string | undefined, value: string, imageURLs: string[]) => {
      const drafts = getAllDrafts();
      const key = getDraftKey(postId, parentId);

      drafts[key] = {
        postId,
        parentId,
        value,
        imageURLs,
        savedAt: Date.now(),
      };

      setAllDrafts(drafts);
    },
    []
  );

  const getDraft = useCallback(
    (postId: string, parentId?: string): CommentDraft | null => {
      const drafts = getAllDrafts();
      const key = getDraftKey(postId, parentId);
      const draft = drafts[key];

      if (!draft) return null;

      // Check expiry
      if (isExpired(draft.savedAt)) {
        // Clean up expired draft
        delete drafts[key];
        setAllDrafts(drafts);
        return null;
      }

      return draft;
    },
    []
  );

  const clearDraft = useCallback((postId: string, parentId?: string) => {
    const drafts = getAllDrafts();
    const key = getDraftKey(postId, parentId);
    delete drafts[key];
    setAllDrafts(drafts);
  }, []);

  return {
    saveDraft,
    getDraft,
    clearDraft,
  };
}
